<script setup lang="ts">
import type { LawAndCommentary } from '~/utils/solr/law-and-commentary'

const props = defineProps<{
  comment: LawAndCommentary
}>()

const { $parseStringToDocument } = useNuxtApp()
const site = useSiteConfig()

const literatureListRef = useTemplateRef<HTMLElement>('literatureList')

const formattedBodytext = computed(() => {
  const parsed = $parseStringToDocument(props.comment?.bodytext, ['clean-corrupted-attributes', 'anchor-target-blank'], {
    siteUrl: site.url,
  })

  return parsed.body.innerHTML
})

// Parse entries separated by <p>&nbsp;</p>
const entries = computed(() => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(formattedBodytext.value, 'text/html')
  const paragraphs = Array.from(doc.body.children)

  const result: Array<{ html: string, paragraphCount: number }> = []
  let currentEntry: Element[] = []

  paragraphs.forEach((p) => {
    // Check if this is a separator paragraph (contains only &nbsp; or is empty)
    const isSeparator = p.innerHTML.trim() === '&nbsp;' || p.textContent?.trim() === ''

    if (isSeparator) {
      if (currentEntry.length > 0) {
        // Save the current entry
        const entryHtml = currentEntry.map(el => el.outerHTML).join('')
        result.push({
          html: entryHtml,
          paragraphCount: currentEntry.length,
        })
        currentEntry = []
      }
    }
    else {
      // Add to current entry
      currentEntry.push(p)
    }
  })

  // Don't forget the last entry if there's no trailing separator
  if (currentEntry.length > 0) {
    const entryHtml = currentEntry.map(el => el.outerHTML).join('')
    result.push({
      html: entryHtml,
      paragraphCount: currentEntry.length,
    })
  }

  return result
})

useFootnotes(literatureListRef)
useInternalLinkHandler(literatureListRef, 'aside')
</script>

<template>
  <div>
    <div
      :id="comment.id"
      ref="literatureList"
      :key="comment.id"
      class="flex flex-col gap-m max-w-3xl mx-auto px-m"
      data-citation-source="literature-list"
    >
      <div
        v-for="(entry, index) in entries"
        :key="index"
        class="literature-entry"
        :class="{
          'single-paragraph': entry.paragraphCount === 1,
          'multi-paragraph': entry.paragraphCount > 1,
        }"
      >
        <BodyText source="ibexa" :html="entry.html" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
/* Single paragraph entries: hanging indent from line 2 */
.literature-entry.single-paragraph :deep(.body-text > p) {
  padding-left: 2rem !important;
  text-indent: -2rem !important;
  margin-bottom: 0 !important;
}

/* Multi-paragraph entries: first paragraph normal, second+ indented */
.literature-entry.multi-paragraph :deep(.body-text) {
  > p {
    margin-bottom: 0.25rem !important;
  }

  > p:first-child {
    padding-left: 0 !important;
    text-indent: 0 !important;
  }

  > p:not(:first-child) {
    padding-left: 2rem !important;
    text-indent: 0 !important;
  }

  > p:last-child {
    margin-bottom: 0 !important;
  }
}

.literature-entry :deep(.body-text) {
  a {
    @apply break-all dark:text-white;
  }

  p {
    @apply dark:text-white;
  }
}
</style>
